import { Box } from '@mui/material';
import { useState, useRef, useEffect, useCallback, useMemo } from 'react';
import { setPendingQuoteLine } from 'hooks/pendingQuoteLine';
import useDebouncedFunc from 'hooks/useDebouncedFunc';
import { useFeatures } from 'hooks/useFeatures';
import useHandleCustomSchemeAndQuery from 'hooks/useHandleCustomSchemeAndQuery';
import useIsViewport from 'hooks/useIsViewport';
import useOfflineQuery from 'hooks/useOfflineQuery';
import useStitchedResult from 'hooks/useStitchedResult';
import { DEFAULT_PAGE_NUMBER } from 'utils/constants';
import { ghostClickDebounce } from 'utils/ghostClickDebounce';
import isSearchableQuery from 'utils/is-searchable-query';
import { FormType, SelectOption, useFormSearchQuery } from 'generated/graphql';
import DesktopSearchResults from './DesktopSearchResults';
import InlineSearchResults from './InlineSearchResults';
import MobileSearchResults from './MobileSearchResults';
import SearchInputField from './SearchInputField';
import loadMoreIndex from './loadMoreIndex';
import { makeScrollRoom, resultsHeight, scrollToSet } from './scrollToSet';
import stitchSearchPages from './stitchSearchPages';

interface FormSearchFieldProps {
  formType: FormType;
  fieldID: string;
  fieldEntityID: string;
  value?: string;
  isDisabled?: boolean;
  onChange: (value: string, values?: string[], entityID?: string) => void;
  onRefresh?: () => void;
}

const FormSearchField = ({
  formType,
  fieldID,
  fieldEntityID,
  value = '',
  isDisabled = false,
  onChange,
  onRefresh,
}: FormSearchFieldProps) => {
  const { isEnabled } = useFeatures();
  const isQuotesSearchEnabled = isEnabled('QuotesSearch');

  const isMobile = useIsViewport();
  const [inputValue, setInputValue] = useState(value);
  const [searchValue, setSearchValue] = useState(value);
  const [pageNumber, setPageNumber] = useState(DEFAULT_PAGE_NUMBER);
  const [filteredOptions, setFilteredOptions] = useState<SelectOption[]>([]);
  const [open, setOpen] = useState(false);
  const anchorRef = useRef<HTMLDivElement | null>(null);
  const { handleSchemeOrQuery } = useHandleCustomSchemeAndQuery({ onMutate: onRefresh });
  // Behind QuotesSearch a query too short to be useful is never sent and shows no results.
  const isSearchable = !isQuotesSearchEnabled || isSearchableQuery(searchValue);

  const [queryResult] = useOfflineQuery(`form-${fieldID}-query`, useFormSearchQuery, {
    variables: {
      input: {
        fieldID,
        entityID: fieldEntityID,
        search: searchValue.toLowerCase(),
        type: formType,
        page: pageNumber,
      },
    },
    pause: !fieldID || !isSearchable,
    requestPolicy: 'cache-and-network',
  });

  const { data, fetching } = queryResult;
  const [stitchedResults] = useStitchedResult(data?.formSearch, stitchSearchPages);
  const searchResults = isQuotesSearchEnabled ? stitchedResults : data?.formSearch;
  // The card's button is how the backend offers adding the search text. A paused query keeps the last result, whose
  // action names the previous search.
  const addAction = isSearchable ? searchResults?.card?.button?.actions?.[0] : undefined;
  const hasNextPage = isQuotesSearchEnabled && isSearchable && !!searchResults?.page?.hasNextPage;
  const isFetchingMore = fetching && pageNumber > DEFAULT_PAGE_NUMBER;
  const pageSize = searchResults?.page?.pageSize || filteredOptions.length;

  useEffect(() => {
    setFilteredOptions(isSearchable ? (searchResults?.results ?? []) : []);
  }, [searchResults, isSearchable]);

  const handleLoadMore = useCallback(() => {
    setPageNumber((searchResults?.page?.page ?? DEFAULT_PAGE_NUMBER) + 1);
  }, [searchResults]);

  const handleClear = useCallback(() => setInputValue(''), []);
  const onClear = isQuotesSearchEnabled ? handleClear : undefined;

  const handleInputChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const value = event.target.value;
      setInputValue(value);
      setOpen(!!value || isMobile);
    },
    [isMobile],
  );

  const handleSearch = useCallback((value: string) => {
    setSearchValue(value);
    setPageNumber(DEFAULT_PAGE_NUMBER);
  }, []);

  const debouncedSearch = useDebouncedFunc(handleSearch, 300);

  const isScrollingToSet = isQuotesSearchEnabled && !isMobile;
  const hasInput = inputValue !== '';
  const [inlineResultsHeight, setInlineResultsHeight] = useState<number>();
  useEffect(() => {
    if (!isScrollingToSet || !hasInput) return undefined;
    return makeScrollRoom(anchorRef.current);
  }, [isScrollingToSet, hasInput]);
  useEffect(() => {
    if (!isScrollingToSet || !hasInput) {
      setInlineResultsHeight(undefined);
      return;
    }
    setInlineResultsHeight(resultsHeight(anchorRef.current));
  }, [isScrollingToSet, hasInput]);
  useEffect(() => {
    if (inlineResultsHeight) scrollToSet(anchorRef.current);
  }, [inlineResultsHeight]);

  useEffect(() => {
    debouncedSearch(inputValue);
  }, [inputValue, debouncedSearch]);

  const handleResultClick = useCallback(
    (option: string, optionEntityID: string) => {
      setInputValue('');
      setOpen(false);
      if (isQuotesSearchEnabled && formType === FormType.SetQuote) setPendingQuoteLine(option);
      onChange(option, [option], optionEntityID);
    },
    [onChange, isQuotesSearchEnabled, formType],
  );

  const handleAdd = useCallback(() => {
    setOpen(false);
    setInputValue('');
    if (addAction?.url || addAction?.query) handleSchemeOrQuery(addAction.url, addAction.query);
    else onChange(inputValue);
  }, [inputValue, addAction, onChange, handleSchemeOrQuery]);
  // Behind QuotesSearch only a backend add action offers adding; without it every search offers the typed text.
  const canAdd = !isQuotesSearchEnabled || !!addAction;

  const handleInputFocus = useCallback(() => {
    ghostClickDebounce(() => setOpen(true)); // Small delay to avoid any ghost clicks when dialog or popper opens
  }, []);

  const sharedSearchResultProps = useMemo(
    () => ({
      open,
      anchorRef,
      inputValue,
      filteredOptions,
      onResultClick: handleResultClick,
      onAdd: canAdd ? handleAdd : undefined,
      setOpen,
      isFetching: fetching && !isFetchingMore,
      isDisabled,
      hasNextPage,
      isFetchingMore,
      loadMoreIndex: loadMoreIndex(filteredOptions.length, pageSize),
      onLoadMore: handleLoadMore,
    }),
    [
      open,
      anchorRef,
      inputValue,
      filteredOptions,
      handleResultClick,
      canAdd,
      handleAdd,
      setOpen,
      fetching,
      isDisabled,
      hasNextPage,
      isFetchingMore,
      pageSize,
      handleLoadMore,
    ],
  );

  return (
    <>
      <Box ref={anchorRef}>
        <SearchInputField
          inputValue={inputValue}
          onInputChange={handleInputChange}
          onClick={handleInputFocus}
          onClear={onClear}
          isDisabled={isDisabled}
          isFetching={fetching}
        />
      </Box>

      {isMobile ? (
        <MobileSearchResults
          {...sharedSearchResultProps}
          onInputChange={handleInputChange}
          onClear={onClear}
          onFieldClick={handleInputFocus}
        />
      ) : isQuotesSearchEnabled ? (
        <InlineSearchResults {...sharedSearchResultProps} height={inlineResultsHeight} />
      ) : (
        <DesktopSearchResults {...sharedSearchResultProps} />
      )}
    </>
  );
};

export default FormSearchField;
